/**
 * Client-side validation for the in-app auth forms (FR-2.11). Catches obviously-bad
 * input before a network round trip and mirrors the Cognito pool's password policy
 * (`minLength: 8, requireDigits, requireLowercase` — see the CDK `passwordPolicy`) so
 * the copy matches what the pool would reject. Each returns friendly copy, or `null`
 * when the value is acceptable.
 */

import {
  isUnderMinimumAge,
  isValidDateOfBirth,
  type CalendarDate,
  type DateOfBirth,
} from '@jeopardy/shared';

/** Mirrors the pool password policy (min 8, a digit, a lowercase letter). */
export function validatePassword(password: string): string | null {
  if (password.length < 8) return 'Use at least 8 characters.';
  if (!/[0-9]/.test(password)) return 'Include at least one number.';
  if (!/[a-z]/.test(password)) return 'Include at least one lowercase letter.';
  return null;
}

/** A permissive email shape check (Cognito does the authoritative validation). */
export function validateEmail(email: string): string | null {
  if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
    return 'Enter a valid email address.';
  }
  // Mirror the server's account-reuse guard (#171, NFR-5.7): a `+` in the local-part
  // (plus/sub-addressing) is rejected so one inbox can't mint unlimited aliases to farm
  // the free signup grant. The server stays authoritative; this is a friendly pre-check.
  if (email.slice(0, email.indexOf('@')).includes('+')) {
    return 'Email aliases with a "+" are not allowed — use your primary email address.';
  }
  return null;
}

/** A username must be non-empty. */
export function validateUsername(username: string): string | null {
  if (username.trim().length === 0) return 'Enter a username.';
  return null;
}

/** A verification/reset code must be non-empty. */
export function validateCode(code: string): string | null {
  if (code.trim().length === 0) return 'Enter the code we emailed you.';
  return null;
}

/** Today as a {@link CalendarDate} for the clock-free shared age helpers. */
function today(): CalendarDate {
  const n = new Date();
  return { year: n.getFullYear(), month: n.getMonth() + 1, day: n.getDate() };
}

/**
 * Parse a native `<input type="date">` value (`YYYY-MM-DD`) into a {@link DateOfBirth},
 * or `null` when it isn't that shape. Calendar validity + range are left to the shared
 * `isValidDateOfBirth`.
 */
export function parseDateOfBirthInput(value: string): DateOfBirth | null {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value.trim());
  if (!m) return null;
  return { year: Number(m[1]), month: Number(m[2]), day: Number(m[3]) };
}

/**
 * Validate the registration date of birth (FR-14.1, #41). Mirrors the server-authoritative
 * gate so an obviously-bad or under-13 value is caught before the round trip; the server
 * remains the authority. Returns friendly copy, or `null` when the DOB is acceptable.
 */
export function validateDateOfBirth(value: string): string | null {
  const dob = parseDateOfBirthInput(value);
  const now = today();
  if (!dob || !isValidDateOfBirth(dob, now)) return 'Enter a valid date of birth.';
  if (isUnderMinimumAge(dob, now)) {
    return 'You must be at least 13 years old to create an account.';
  }
  return null;
}
